const CHUNK_RELOAD_STORAGE_KEY_PREFIX = "wpx:lazy-chunk-reload:";

const CHUNK_LOAD_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "Loading chunk ",
];

const getChunkReloadStorageKey = (pathname: string): string => {
  return `${CHUNK_RELOAD_STORAGE_KEY_PREFIX}${pathname}`;
};

const getErrorMessage = (error: unknown): string => {
  return error instanceof Error ? error.message : String(error);
};

/**
 * Detects browser/runtime failures caused by a stale or unavailable lazy-loaded
 * JavaScript chunk.
 *
 * @param error Error value captured by React or Sentry.
 * @returns Whether the value matches a known lazy chunk load failure.
 */
export const isChunkLoadFailure = (error: unknown): boolean => {
  const errorMessage = getErrorMessage(error);

  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => {
    return errorMessage.includes(pattern);
  });
};

/**
 * Returns whether the current route already consumed its one automatic reload
 * for a lazy chunk failure.
 *
 * @param pathname Current browser route pathname.
 * @returns Whether a reload was already attempted for the route.
 */
export const hasChunkLoadReloadAttempt = (pathname: string): boolean => {
  try {
    return (
      window.sessionStorage.getItem(getChunkReloadStorageKey(pathname)) === "1"
    );
  } catch {
    return false;
  }
};

/**
 * Claims the one automatic route reload used to recover stale Vite chunks.
 *
 * @param pathname Current browser route pathname.
 * @returns Whether the reload claim was stored successfully.
 */
export const claimChunkLoadReload = (pathname: string): boolean => {
  if (hasChunkLoadReloadAttempt(pathname)) {
    return false;
  }

  try {
    window.sessionStorage.setItem(getChunkReloadStorageKey(pathname), "1");
    return true;
  } catch {
    return false;
  }
};
